import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Paper,
  IconButton,
  Slider,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  PlayArrow,
  Pause,
  VolumeUp,
  VolumeOff,
} from '@mui/icons-material';
import WaveSurfer from 'wavesurfer.js';
import { apiService } from '../../services/api';

/**
 * AudioPlayer Component
 *
 * Standalone audio player with waveform visualization and playback controls.
 * Can be used independently anywhere in the app.
 *
 * Features:
 * - Wavesurfer.js waveform visualization
 * - Play/pause controls
 * - Playback speed adjustment
 * - Volume control
 * - Time display
 * - File selector for multiple files
 */
const AudioPlayer = ({ audioFiles = [], selectedFileId = null, onFileChange = null }) => {
  // Refs
  const waveformRef = useRef(null);
  const wavesurferRef = useRef(null);

  // State
  const [currentFileId, setCurrentFileId] = useState(selectedFileId);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Update current file when prop changes
  useEffect(() => {
    if (selectedFileId && selectedFileId !== currentFileId) {
      setCurrentFileId(selectedFileId);
    }
  }, [selectedFileId, currentFileId]);

  // Auto-select first file if none selected
  useEffect(() => {
    if (!currentFileId && audioFiles.length > 0) {
      setCurrentFileId(audioFiles[0].id);
    }
  }, [audioFiles, currentFileId]);

  // Initialize/Update Wavesurfer when file changes
  useEffect(() => {
    if (!waveformRef.current || !currentFileId) return;

    setLoading(true);
    setError(null);

    // Destroy existing instance
    if (wavesurferRef.current) {
      wavesurferRef.current.destroy();
    }

    // Create new Wavesurfer instance
    const wavesurfer = WaveSurfer.create({
      container: waveformRef.current,
      waveColor: '#ddd',
      progressColor: '#1976d2',
      cursorColor: '#1976d2',
      barWidth: 2,
      barRadius: 3,
      cursorWidth: 2,
      height: 100,
      barGap: 2,
      normalize: true,
      backend: 'WebAudio',
    });

    wavesurferRef.current = wavesurfer;

    // Set up event listeners BEFORE loading to catch early errors
    wavesurfer.on('ready', () => {
      setDuration(wavesurfer.getDuration());
      setLoading(false);
    });

    wavesurfer.on('audioprocess', () => {
      setCurrentTime(wavesurfer.getCurrentTime());
    });

    wavesurfer.on('seek', () => {
      setCurrentTime(wavesurfer.getCurrentTime());
    });

    wavesurfer.on('play', () => {
      setIsPlaying(true);
    });

    wavesurfer.on('pause', () => {
      setIsPlaying(false);
    });

    wavesurfer.on('error', (err) => {
      console.error('Wavesurfer error:', err);
      setError('Unable to load audio file. The file may not exist or may be corrupted.');
      setLoading(false);
    });

    // Load audio from streaming endpoint (after error handler is set up)
    const audioUrl = apiService.getAudioStreamUrl(currentFileId);

    // Track blob URL for cleanup
    let blobUrl = null;

    // Fetch and load audio with authentication
    const checkAndLoadAudio = async () => {
      try {
        // Get JWT token from localStorage for authentication
        const token = localStorage.getItem('authToken');
        const headers = {};

        // Add Authorization header if token exists
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }

        // Fetch the entire audio file with authentication
        const response = await fetch(audioUrl, {
          method: 'GET',
          headers: headers,
        });

        if (!response.ok) {
          setError(`Audio file not available (${response.status}). The file may have been deleted.`);
          setLoading(false);
          return;
        }

        // Convert response to blob
        const blob = await response.blob();
        blobUrl = URL.createObjectURL(blob);

        // Load blob URL into wavesurfer (no auth needed for blob URLs)
        wavesurfer.load(blobUrl);
      } catch (err) {
        console.error('Error checking/loading audio:', err);
        setError('Unable to load audio file. The file may not exist or connection failed.');
        setLoading(false);
      }
    };

    checkAndLoadAudio();

    // Cleanup
    return () => {
      if (wavesurferRef.current) {
        wavesurferRef.current.destroy();
      }
      // Clean up blob URL if it was created
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
      }
    };
  }, [currentFileId]);

  // Playback controls
  const togglePlayPause = () => {
    if (wavesurferRef.current) {
      wavesurferRef.current.playPause();
    }
  };

  const handleVolumeChange = (_event, newValue) => {
    setVolume(newValue);
    if (wavesurferRef.current) {
      wavesurferRef.current.setVolume(newValue);
    }
  };

  const toggleMute = () => {
    setIsMuted(!isMuted);
    if (wavesurferRef.current) {
      wavesurferRef.current.setMuted(!isMuted);
    }
  };

  const handlePlaybackRateChange = (event) => {
    const rate = event.target.value;
    setPlaybackRate(rate);
    if (wavesurferRef.current) {
      wavesurferRef.current.setPlaybackRate(rate);
    }
  };

  const handleFileChange = (event) => {
    const newFileId = event.target.value;
    setCurrentFileId(newFileId);
    if (onFileChange) {
      onFileChange(newFileId);
    }
  };

  // Format time display
  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Get current file info
  const currentFile = audioFiles.find(f => f.id === currentFileId);

  // Don't render if no files
  if (audioFiles.length === 0) {
    return null;
  }

  return (
    <Paper elevation={2} sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" sx={{ flexGrow: 1 }}>
          Audio Player
        </Typography>

        {/* File selector if multiple files */}
        {audioFiles.length > 1 && (
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Select File</InputLabel>
            <Select
              value={currentFileId || ''}
              label="Select File"
              onChange={handleFileChange}
            >
              {audioFiles.map((file) => (
                <MenuItem key={file.id} value={file.id}>
                  {file.original_filename}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </Box>

      {/* File info */}
      {currentFile && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          {currentFile.original_filename} • {Math.floor(currentFile.duration / 60)}:{(currentFile.duration % 60).toString().padStart(2, '0')}
        </Typography>
      )}

      {/* Error display */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Loading indicator */}
      {loading && (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="100px">
          <CircularProgress size={40} />
        </Box>
      )}

      {/* Waveform */}
      <Box ref={waveformRef} sx={{ mb: 2, display: loading ? 'none' : 'block' }} />

      {/* Playback Controls */}
      {!loading && (
        <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
          {/* Play/Pause */}
          <IconButton onClick={togglePlayPause} color="primary" size="large">
            {isPlaying ? <Pause /> : <PlayArrow />}
          </IconButton>

          {/* Time Display */}
          <Typography variant="body2" sx={{ minWidth: 100 }}>
            {formatTime(currentTime)} / {formatTime(duration)}
          </Typography>

          {/* Playback Speed */}
          <FormControl size="small" sx={{ minWidth: 100 }}>
            <InputLabel>Speed</InputLabel>
            <Select
              value={playbackRate}
              label="Speed"
              onChange={handlePlaybackRateChange}
            >
              <MenuItem value={0.5}>0.5x</MenuItem>
              <MenuItem value={0.75}>0.75x</MenuItem>
              <MenuItem value={1}>1x</MenuItem>
              <MenuItem value={1.25}>1.25x</MenuItem>
              <MenuItem value={1.5}>1.5x</MenuItem>
              <MenuItem value={2}>2x</MenuItem>
            </Select>
          </FormControl>

          {/* Volume Control */}
          <Box display="flex" alignItems="center" sx={{ minWidth: 150 }}>
            <IconButton onClick={toggleMute} size="small">
              {isMuted || volume === 0 ? <VolumeOff /> : <VolumeUp />}
            </IconButton>
            <Slider
              value={volume}
              min={0}
              max={1}
              step={0.1}
              onChange={handleVolumeChange}
              sx={{ ml: 1 }}
            />
          </Box>
        </Box>
      )}
    </Paper>
  );
};

export default AudioPlayer;
